// ============================================================================
// HtmlEditor — TipTap WYSIWYG wrapper for email templates
// PV-1052 · M12 · ADR-033 §E2
// ----------------------------------------------------------------------------
// Minimal toolbar (B/I/U/Link/lists/undo/redo) + variable chip insertion.
// Output HTML is saved verbatim to email_templates.body_html — the branded
// layout wrapper (header/footer/RTL) is applied at render time by
// wrapEmailLayout (see src/lib/email-preview/layout.ts).
//
// No tables/images/colors on purpose: the shell owns branding, the body
// only carries the editorial copy. This keeps mail-client compat
// (Gmail/Outlook/Apple Mail) predictable.
// ============================================================================

import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { forwardRef, useCallback, useEffect, useImperativeHandle, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

// Visual styles for `.html-editor*` live in src/styles/admin-emails.css.
// AdminEmailsPage imports that file once, so any consumer of HtmlEditor in
// the admin emails feature inherits them automatically. If HtmlEditor is
// reused on another page later, extract the `.html-editor*` block into a
// shared CSS file (e.g. src/styles/html-editor.css) and import it from
// whichever lazy chunk owns the page.

export type HtmlEditorVariable = {
  name: string;
  label?: string;
};

export type HtmlEditorRef = {
  insertVariable: (name: string) => void;
  focus: () => void;
};

export type HtmlEditorProps = {
  value: string;
  onChange: (html: string) => void;
  variables?: HtmlEditorVariable[];
  dir?: 'ltr' | 'rtl';
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
};

function HtmlEditorImpl(
  {
    value,
    onChange,
    variables = [],
    dir = 'ltr',
    placeholder,
    ariaLabel,
    className,
    style,
  }: HtmlEditorProps,
  ref: React.Ref<HtmlEditorRef>,
) {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener', target: '_blank' },
        },
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'html-editor__surface',
        dir,
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': ariaLabel ?? '',
      },
    },
    onUpdate({ editor: ed }: { editor: Editor }) {
      onChange(ed.getHTML());
    },
  });

  // Keep editor in sync when external value changes (locale switch).
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  // Keep dir attr in sync without recreating the editor.
  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom as HTMLElement;
    dom.setAttribute('dir', dir);
  }, [editor, dir]);

  useImperativeHandle(
    ref,
    () => ({
      insertVariable(name: string) {
        editor?.chain().focus().insertContent(`{{${name}}}`).run();
      },
      focus() {
        editor?.commands.focus();
      },
    }),
    [editor],
  );

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href as string | undefined;
    const next = window.prompt(t('admin.emails.editor.linkPrompt'), previous ?? 'https://');
    if (next === null) return;
    if (next === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: next }).run();
  }, [editor, t]);

  if (!editor) {
    return <div className={`html-editor html-editor--loading ${className ?? ''}`} style={style} />;
  }

  return (
    <div className={`html-editor ${className ?? ''}`} style={style}>
      <div
        className="html-editor__toolbar"
        role="toolbar"
        aria-label={t('admin.emails.editor.toolbarAria')}
      >
        <button
          type="button"
          className={`html-editor__btn ${editor.isActive('bold') ? 'is-active' : ''}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          aria-pressed={editor.isActive('bold')}
          aria-label={t('admin.emails.editor.bold')}
          title={t('admin.emails.editor.bold')}
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          className={`html-editor__btn ${editor.isActive('italic') ? 'is-active' : ''}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          aria-pressed={editor.isActive('italic')}
          aria-label={t('admin.emails.editor.italic')}
          title={t('admin.emails.editor.italic')}
        >
          <em>I</em>
        </button>
        <button
          type="button"
          className={`html-editor__btn ${editor.isActive('underline') ? 'is-active' : ''}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          aria-pressed={editor.isActive('underline')}
          aria-label={t('admin.emails.editor.underline')}
          title={t('admin.emails.editor.underline')}
        >
          <u>U</u>
        </button>
        <span className="html-editor__sep" aria-hidden="true" />
        <button
          type="button"
          className={`html-editor__btn ${editor.isActive('link') ? 'is-active' : ''}`}
          onClick={setLink}
          aria-label={t('admin.emails.editor.link')}
          title={t('admin.emails.editor.link')}
        >
          🔗
        </button>
        <button
          type="button"
          className={`html-editor__btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          aria-label={t('admin.emails.editor.bulletList')}
          title={t('admin.emails.editor.bulletList')}
        >
          •
        </button>
        <button
          type="button"
          className={`html-editor__btn ${editor.isActive('orderedList') ? 'is-active' : ''}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          aria-label={t('admin.emails.editor.orderedList')}
          title={t('admin.emails.editor.orderedList')}
        >
          1.
        </button>
        <span className="html-editor__sep" aria-hidden="true" />
        <button
          type="button"
          className="html-editor__btn"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          aria-label={t('admin.emails.editor.undo')}
          title={t('admin.emails.editor.undo')}
        >
          ↶
        </button>
        <button
          type="button"
          className="html-editor__btn"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          aria-label={t('admin.emails.editor.redo')}
          title={t('admin.emails.editor.redo')}
        >
          ↷
        </button>
      </div>

      {variables.length > 0 ? (
        <div
          className="html-editor__variables"
          role="group"
          aria-label={t('admin.emails.editor.variablesAria')}
        >
          <span className="html-editor__variables-label">
            {t('admin.emails.editor.insertVariable')}
          </span>
          {variables.map((v) => (
            <button
              key={v.name}
              type="button"
              className="html-editor__chip"
              onClick={() => editor.chain().focus().insertContent(`{{${v.name}}}`).run()}
              aria-label={t('admin.emails.editor.insertVariableAria', { name: v.name })}
            >
              {v.label ?? `{{${v.name}}}`}
            </button>
          ))}
        </div>
      ) : null}

      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}

export const HtmlEditor = forwardRef<HtmlEditorRef, HtmlEditorProps>(HtmlEditorImpl);
